// Dates: occurrences, leads and how a date is said. Mirrors
// internal/kinds/date (Next, Between, Years, Lead) and reconcile.DateLine, so
// the app says what the push says.
import { DEFAULT_LEADS, type DateCategory, type DateSpec } from '@/kinds/date/schema'
import { addDays, daysBetween, parseDate, todayLocal } from './time'

const pad = (n: number) => String(n).padStart(2, '0')

/** Day d of month m (1-based) in year y, clamped to the month's last day. */
const on = (y: number, m: number, d: number): string => {
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return `${y}-${pad(m)}-${pad(Math.min(d, last))}`
}

/**
 * The first occurrence on or after `from`, or '' when there is none left: a
 * one-off that has passed, or a repeat past its `until`. A monthly date past a
 * short month's end falls on its last day; 29 February falls on the 28th in
 * other years; skipped occurrences are passed over. Mirrors date.Spec.Next.
 */
export const nextOccurrence = (
  sp: Pick<DateSpec, 'date' | 'repeat' | 'until' | 'skip'>,
  from: string,
): string => {
  let d = rawNext(sp, from)
  for (let i = 0; d && i < 400 && sp.skip?.includes(d); i++) d = rawNext(sp, addDays(d, 1))
  if (d && sp.until && d > sp.until) return ''
  return d
}

const rawNext = (sp: Pick<DateSpec, 'date' | 'repeat'>, from: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(sp.date)) return ''
  const [by, bm, bd] = sp.date.split('-').map(Number) as [number, number, number]
  const fy = Number(from.slice(0, 4))
  switch (sp.repeat) {
    case 'weekly':
    case 'fortnightly': {
      const step = sp.repeat === 'fortnightly' ? 14 : 7
      if (from <= sp.date) return sp.date
      return addDays(sp.date, Math.ceil(daysBetween(sp.date, from) / step) * step)
    }
    case 'yearly': {
      const first = Math.max(fy, by)
      for (let y = first; y <= first + 1; y++) {
        const d = on(y, bm, bd)
        if (d >= from && d >= sp.date) return d
      }
      return ''
    }
    case 'monthly': {
      const start = from < sp.date ? sp.date : from
      const [sy, sm] = start.split('-').map(Number) as [number, number]
      for (let i = 0; i < 2; i++) {
        const m0 = sm - 1 + i
        const d = on(sy + Math.floor(m0 / 12), (m0 % 12) + 1, bd)
        if (d >= start) return d
      }
      return ''
    }
    default:
      return sp.date < from ? '' : sp.date
  }
}

/** The occurrences from..to inclusive. */
export const occurrencesBetween = (
  sp: Pick<DateSpec, 'date' | 'repeat' | 'until' | 'skip'>,
  from: string,
  to: string,
): string[] => {
  const out: string[] = []
  for (let d = nextOccurrence(sp, from); d && d <= to; d = nextOccurrence(sp, addDays(d, 1))) out.push(d)
  return out
}

/** Years from the original to an occurrence of a yearly date (an age); 0 when unknown. */
export const yearsAt = (sp: Pick<DateSpec, 'date' | 'repeat' | 'no_year'>, occurrence: string): number => {
  if (sp.repeat !== 'yearly' || sp.no_year) return 0
  const n = Number(occurrence.slice(0, 4)) - Number(sp.date.slice(0, 4))
  return n > 0 ? n : 0
}

/** Lead days: the date's own, else Settings for its category, else the default. */
export const leadOf = (
  sp: Pick<DateSpec, 'lead_days' | 'category'>,
  leads?: Record<string, number>,
): number => sp.lead_days ?? leads?.[sp.category] ?? DEFAULT_LEADS[sp.category]

const isDue = (c: DateCategory) => c === 'bill' || c === 'deadline'

/** "turns 36", "5 years", or '' */
export const yearsPhrase = (sp: DateSpec, occurrence: string): string => {
  const y = yearsAt(sp, occurrence)
  if (!y) return ''
  if (sp.category === 'birthday') return `turns ${y}`
  if (sp.category === 'anniversary') return `${y} ${y === 1 ? 'year' : 'years'}`
  return ''
}

/** Days from today to the occurrence, in words: Today, Tomorrow, In 5 days. */
export const whenPhrase = (occurrence: string, today = todayLocal()): string => {
  const n = daysBetween(today, occurrence)
  if (n === 0) return 'Today'
  if (n === 1) return 'Tomorrow'
  return `In ${n} days`
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Thu 1 Oct", as Go's "Mon 2 Jan" says it (the locale's own may say "Sept"). */
export const shortDay = (date: string): string => {
  const t = parseDate(date)
  return `${DAYS[t.getDay()]} ${t.getDate()} ${MONTHS[t.getMonth()]}`
}

/**
 * The push's line for an occurrence seen from today (reconcile.DateLine):
 * "In 7 days, Thu 1 Oct, turns 36", "Due tomorrow", "Today at 14:15".
 */
export const dateLine = (sp: DateSpec, occurrence: string, today = todayLocal()): string => {
  const n = daysBetween(today, occurrence)
  let when = n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : `In ${n} days, ${shortDay(occurrence)}`
  if (isDue(sp.category)) when = 'Due ' + when[0]!.toLowerCase() + when.slice(1)
  if (sp.time) when += ` at ${sp.time}`
  const y = yearsPhrase(sp, occurrence)
  return y ? `${when}, ${y}` : when
}

export interface Upcoming<R> {
  date: R
  /** the next occurrence, YYYY-MM-DD */
  on: string
  /** days from today */
  days: number
}

/** Dates whose next occurrence falls within `within` days, soonest first. */
export const upcomingDates = <R extends { spec: DateSpec; deleted_at?: string | null }>(
  rows: R[],
  today = todayLocal(),
  within = 366,
): Upcoming<R>[] => {
  const last = addDays(today, within)
  const out: Upcoming<R>[] = []
  for (const r of rows) {
    if (r.deleted_at) continue
    const on = nextOccurrence(r.spec, today)
    if (on && on <= last) out.push({ date: r, on, days: daysBetween(today, on) })
  }
  return out.sort(
    (a, b) => a.on.localeCompare(b.on) || (a.date.spec.time ?? '').localeCompare(b.date.spec.time ?? ''),
  )
}

export const CATEGORY_LABEL: Record<DateCategory, string> = {
  birthday: 'Birthday',
  anniversary: 'Anniversary',
  appointment: 'Appointment',
  deadline: 'Deadline',
  bill: 'Bill or renewal',
  countdown: 'Countdown',
  other: 'Other',
}

/** What a new date of this category usually repeats as. */
export const CATEGORY_REPEAT: Record<DateCategory, NonNullable<DateSpec['repeat']>> = {
  birthday: 'yearly',
  anniversary: 'yearly',
  appointment: 'none',
  deadline: 'none',
  bill: 'monthly',
  countdown: 'none',
  other: 'none',
}

/**
 * A row's line where the day itself is shown beside it: "In 7 days, turns 36",
 * "Due tomorrow at 14:15", "Today".
 */
export const rowLine = (sp: DateSpec, occurrence: string, today = todayLocal()): string => {
  let when = whenPhrase(occurrence, today)
  // "Due in 12 days", unless the title says due already ("Car MOT due": "In 12 days")
  if (isDue(sp.category) && !/\bdue\b/i.test(sp.title)) when = 'Due ' + when[0]!.toLowerCase() + when.slice(1)
  if (sp.time) when += ` at ${sp.time}`
  if (sp.place) when += `, ${sp.place}`
  const y = yearsPhrase(sp, occurrence)
  return y ? `${when}, ${y}` : when
}

/** How a date repeats, said plainly. */
export const repeatPhrase = (sp: Pick<DateSpec, 'repeat' | 'date'>): string =>
  sp.repeat === 'weekly'
    ? `Every ${WEEKDAYS[parseDate(sp.date).getDay()]}`
    : sp.repeat === 'fortnightly'
      ? `Every other ${WEEKDAYS[parseDate(sp.date).getDay()]}`
      : sp.repeat === 'yearly'
        ? `Every year on ${Number(sp.date.slice(8, 10))} ${MONTHS[Number(sp.date.slice(5, 7)) - 1]}`
        : sp.repeat === 'monthly'
          ? `Every month on the ${ordinal(Number(sp.date.slice(8, 10)))}`
          : 'Once'

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const ordinal = (n: number) => {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')
  return `${n}${s}`
}

// ---- appointments -------------------------------------------------------------

const toMinute = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))
export const clockAt = (minute: number): string =>
  `${String(Math.floor(minute / 60) % 24).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`

/** How long a timed date lasts: its own length, else an hour (date.Spec.Length). */
export const lengthOf = (sp: Pick<DateSpec, 'minutes'>): number => sp.minutes || 60

/**
 * A row's line at a minute of today: an appointment that has begun says "Now,
 * until 11:00" (and where), one that is over is null so a list can leave it
 * out, anything else is `rowLine`.
 */
export const rowLineAt = (sp: DateSpec, occurrence: string, today: string, minute: number): string | null => {
  if (occurrence !== today || !sp.time) return rowLine(sp, occurrence, today)
  const start = toMinute(sp.time)
  const end = start + lengthOf(sp)
  if (minute >= end) return null
  if (minute < start) return rowLine(sp, occurrence, today)
  return `Now, until ${clockAt(end)}${sp.place ? `, ${sp.place}` : ''}`
}

/** One appointment on a day, in minutes of that day. */
export interface Fixed<R> {
  date: R
  on: string
  /** start, end and (with travel) when to leave, as minutes of the day */
  start: number
  end: number
  leave?: number
}

/** The timed dates falling on a day, earliest first. */
export const fixedOn = <R extends { spec: DateSpec; deleted_at?: string | null }>(
  rows: R[],
  day: string,
): Fixed<R>[] =>
  rows
    .filter((r) => !r.deleted_at && r.spec.time && nextOccurrence(r.spec, day) === day)
    .map((r) => {
      const start = toMinute(r.spec.time!)
      return {
        date: r,
        on: day,
        start,
        end: start + lengthOf(r.spec),
        leave: r.spec.travel_minutes ? start - r.spec.travel_minutes : undefined,
      }
    })
    .sort((a, b) => a.start - b.start)

/** The next appointment still to start today, with the minutes until it. */
export const nextFixed = <R extends { spec: DateSpec; deleted_at?: string | null }>(
  rows: R[],
  now: Date,
): { fixed: Fixed<R>; minutes: number } | undefined => {
  const m = now.getHours() * 60 + now.getMinutes()
  const f = fixedOn(rows, dateOfLocal(now)).find((x) => x.start > m)
  return f ? { fixed: f, minutes: f.start - m } : undefined
}

/** The appointment a time on a day would fall inside, if any: "That's during the dentist". */
export const clashWith = <R extends { spec: DateSpec; deleted_at?: string | null }>(
  rows: R[],
  day: string,
  hhmm: string,
  minutes = 0,
): Fixed<R> | undefined => {
  if (!/^\d{2}:\d{2}$/.test(hhmm)) return undefined
  const a = toMinute(hhmm)
  const b = a + Math.max(minutes, 1)
  return fixedOn(rows, day).find((f) => a < f.end && b > f.start)
}

const dateOfLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
